import { CaravanStatuses } from '@havenengineering/module-caravan-sales-types';
import { getParkByCode } from '@havenengineering/module-shared-library/dist/utils/parks/getters';
import { HolidayType } from '@havenengineering/module-shared-library/dist/utils/parks/types';
import {
  CaravanResponse,
  ExtendedFiltersType,
  Filters,
  IdSet,
  Price,
} from '../../module/caravan-search-page/types';
import { SmartseerResponseMetadata } from '../../types/smartseer';
import { stringifyAndEscapeQuotes } from './gtm-shared';
import { getPageFirstItemIndex } from '../../module/caravan-search-page/helper/page-size';

const EVENTS = {
  VIRTUAL_PAGE_VIEW: 'virtual page view',
  VISITOR_INTERACTION: 'visitor interaction',
};

export const getSortByLabel = (value?: string) => {
  const PITCH_AVAILABILITY_LABEL = 'Default';
  const PRICE_LOW_TO_HIGH_LABEL = 'Price, low to high';
  const PRICE_HIGH_TO_LOW_LABEL = 'Price, high to low';
  const LISTING_AGE_LOW_TO_HIGH_LABEL = 'Listing age, low to high';
  const LISTING_AGE_HIGH_TO_LOW_LABEL = 'Listing age, high to low';

  if (value === 'totalPrice-asc') {
    return PRICE_LOW_TO_HIGH_LABEL;
  }
  if (value === 'totalPrice-desc') {
    return PRICE_HIGH_TO_LOW_LABEL;
  }
  if (value === 'listingAge-asc') {
    return LISTING_AGE_LOW_TO_HIGH_LABEL;
  }
  if (value === 'listingAge-desc') {
    return LISTING_AGE_HIGH_TO_LOW_LABEL;
  }

  return PITCH_AVAILABILITY_LABEL;
};

export const INTERACTION_NAMES = {
  FILTER_CLICK: 'filter click',
  ACCORDION_CLICK: 'accordion click',
  SORT_LIST: 'sort list',
  ACCOMMODATION_SELECT: 'accommodation select',
  BUTTON_CLICK: 'button click',
  ACCOMMODATION_IMPRESSION: 'accommodation impression',
  OVERLAY_IMPRESSION: 'overlay impression',
  SEARCH_SUBMIT: 'search submit',
  REQUEST_BROCHURE: 'brochure request',
  PARK_VISIT: 'arrange a visit',
  GENERAL_ENQUIRY: 'general enquiry',
  CLICK_TO_CALL: 'click to call',
  CLICK_TO_VIEW_PARK_VISIT_FORM: 'click to view park visit form',
};

const PRODUCT_TYPE = 'ownership';
const PAGE_TYPE = 'ownership';
const NOT_SELECTED = 'not selected';
const NOT_APPLIED = 'not applied';
const HOME_RESULTS = 'ownership home results';

export type ImageSource = 'no images' | 'matterport' | 'plot' | 'contentful';

const parseArrayForGTM = (
  items?: (string | undefined)[],
  defaultValue?: string,
  convertToLowercase = true,
) => {
  if (!items || !items.length) return defaultValue;
  const regions = items.map((item) =>
    typeof item === 'string' && convertToLowercase ? item.toLocaleLowerCase() : item,
  );

  return [...new Set(regions)].sort().join('|');
};

export const getImageSource = ({
  matterportImages,
  contentfulImages,
  images,
}: CaravanResponse): ImageSource => {
  if (matterportImages?.web?.length) {
    return 'matterport';
  }
  if (contentfulImages?.length) {
    return 'contentful';
  }
  if (images && images.length > 0) {
    return 'plot';
  }
  return 'no images';
};

export const getCaravanInfoAmplitude = (
  caravan: CaravanResponse,
  { position, list }: { position?: number; list?: string } = {},
) => {
  return {
    list_name: list,
    list_position: position,
    category: 'accommodation',
    type: caravan.isLodge ? 'lodge' : 'static caravan',
    variant: `${caravan.manufacturer.name.toLocaleLowerCase()} ${caravan.name.toLocaleLowerCase()} ${
      caravan.yearMade
    }`,
    price: caravan.totalPrice ? caravan.totalPrice : '',
    availability_flag: 'in stock',
    park_code: caravan.park?.parkCode,
    park_name: caravan.park?.name?.toLocaleLowerCase(),
    park_region: caravan.region?.name?.toLocaleLowerCase(),
    bedrooms: caravan.numberOfBeds,
    sleeps: caravan.sleeps,
    image_source: getImageSource(caravan),
    tags: caravan.featurePills
      ?.map((pill) => pill.text.toLowerCase())
      .sort((a, b) => a.localeCompare(b))
      .join('|'),
    central_heating: caravan.hasCentralHeating ? 'yes' : 'no',
    double_glazing: caravan.hasDoubleGlazing ? 'yes' : 'no',
    van_id: caravan.id,
    van_manufacturer: caravan.manufacturer.name.toLocaleLowerCase(),
    van_model: caravan.name.toLocaleLowerCase(),
    van_age: new Date().getFullYear() - caravan.yearMade,
    van_year: String(caravan.yearMade),
    van_condition: caravan.isNew ? 'new' : 'used',
    van_dimensions: caravan.size,
    quantity: 1,
  };
};

export const getPhoneClickDataLayer = (phoneNumber: string) => ({
  event: EVENTS.VISITOR_INTERACTION,
  interactionName: INTERACTION_NAMES.CLICK_TO_CALL,
  phoneNumber,
});

const getPrice = ({ min, max }: { min?: number; max?: number; type?: string }) =>
  `£${(min || 0).toLocaleString('en-GB')} - £${(max || 999999).toLocaleString('en-GB')} (total)`;

export const getOtherFiltersDataLayer = (
  filterAction: 'apply' | 'remove',
  filterCategory: string,
  filterValue: string,
) => ({
  event: EVENTS.VISITOR_INTERACTION,
  interactionName: INTERACTION_NAMES.FILTER_CLICK,
  productType: PRODUCT_TYPE,
  filterAction,
  filterCategory,
  filterValue: filterValue.toLowerCase(),
});

const calculateResultsBatch = (caravans: CaravanResponse[], page = 1) => {
  if (caravans.length) {
    const start = page * 15 - 15 + 1;
    const end = page * 15 - 10;
    const startFormatted = start.toLocaleString('en-US', {
      minimumIntegerDigits: 3,
      useGrouping: false,
    });
    const endFormatted = end.toLocaleString('en-US', {
      minimumIntegerDigits: 3,
      useGrouping: false,
    });
    return `${startFormatted}-${endFormatted}`;
  }
  return undefined;
};

export const getPriceFiltersDataLayer = (price: Price) => ({
  event: EVENTS.VISITOR_INTERACTION,
  interactionName: INTERACTION_NAMES.FILTER_CLICK,
  productType: PRODUCT_TYPE,
  filterAction: 'apply',
  filterCategory: 'price',
  filterValue: getPrice(price),
});

export const getSortVisitorInteractionDataLayer = (newSort: string, oldSort?: string) => {
  const sortBy = getSortByLabel(oldSort).toLowerCase();
  const sortByNew = getSortByLabel(newSort).toLowerCase();

  return {
    event: EVENTS.VISITOR_INTERACTION,
    interactionName: INTERACTION_NAMES.SORT_LIST,
    sortBy,
    sortByNew,
  };
};

const getConditionsFromStatus = (statusIdSet: IdSet<CaravanStatuses> | undefined) => {
  if (!statusIdSet) {
    return new Set();
  }
  const conditionSet: IdSet<string> = new Set();

  if (statusIdSet.has('used')) {
    conditionSet.add('used');
  }
  if (statusIdSet.has('preorder') && statusIdSet.has('new')) {
    conditionSet.add('new');
    conditionSet.add('available to pre-order');
    conditionSet.add('available on park');
  } else if (statusIdSet.has('preorder')) {
    conditionSet.add('available to pre-order');
  } else if (statusIdSet.has('new')) {
    conditionSet.add('available on park');
  }
  return conditionSet;
};

export const getCaravanProductVirtualPageViewDataLayer = (
  caravan: CaravanResponse,
): { [key: string]: string | Record<string, any> | undefined } => ({
  event: EVENTS.VIRTUAL_PAGE_VIEW,
  pageType: 'ownership',
  pageSubType: 'search',
  pageName: '/ga-virtual/ownership/search/home-details',
  pageId: '(no id)',
  productType: PRODUCT_TYPE,
  parkCode: caravan.park?.parkCode.toLocaleLowerCase(), // -> 'undefined' - Discuss these changes with Dan
  parkName: caravan.park?.name?.toLocaleLowerCase(), // -> 'undefined' - Discuss these changes with Dan
  parkRegion: caravan.region?.name?.toLocaleLowerCase(), // -> 'undefined' - Discuss these changes with Dan
  accomType: caravan.isLodge ? 'lodge' : 'static caravan',
  accomManufacturer: caravan.manufacturer.name.toLocaleLowerCase(),
  accomModel: caravan.name.toLocaleLowerCase(),
  accomYear: String(caravan.yearMade),
  accomId: caravan.id,
  accomNewFlag: caravan.isNew ? 'new' : 'used',
  accomSize: caravan.size,
  accomAge: `${new Date().getFullYear() - caravan.yearMade} years old`,
  accomBedrooms: `${caravan.numberOfBeds} bedrooms`,
  accomSleeps: `sleeps ${caravan.sleeps}`,
  accomCentralHeating: `${caravan.hasCentralHeating ? '' : 'no '}central heating`,
  accomDoubleGlazing: `${caravan.hasDoubleGlazing ? '' : 'no '}double glazing`,
  availabilityFlag: caravan.preOrder ? 'pre-order' : 'in stock',
  numSavedVans: caravan?.numSavedVans?.toString() || '0',
  products: [getCaravanInfoAmplitude(caravan)],
  pageFirstPublished: '(not set)',
  pageLastPublished: '(not set)',
  pageAge: '(not set)',
  pagePublishedVersion: '(not set)',
  imageSource: getImageSource(caravan),
});

export const getCaravanSearchVirtualPageViewDataLayer = (
  caravans: CaravanResponse[],
  filterEntries: ExtendedFiltersType,
  filters: Filters,
  count: number,
  SmartseerResponseMetadata: SmartseerResponseMetadata | null = null,
  numSavedVans: number,
  page = 1,
): { [key: string]: string | number | Record<string, any> | undefined | null } => {
  const allParkIds = filterEntries.parkCode.map((park) => park.id);
  const allParkNames = filterEntries.parkCode.map((park) => park.name);
  const allParkRegions = filterEntries.parkCode
    .map((park) => park.regionId)
    .filter((region) => region);

  const bedrooms = Array.from(filters.bedroomsIdSet || new Set()).sort();
  const types = Array.from(filters.typeIdSet || new Set()).sort();
  const statuses = Array.from(getConditionsFromStatus(filters.statusIdSet)).sort();
  const manufacturers = Array.from(filters.manufacturerIdSet || new Set<string>())
    .map((name) => name.toLocaleLowerCase())
    .sort();

  const validSelectedParks = filters.parkIds
    ? Array.from(filters.parkIds).map((parkId) => {
        const parkData = getParkByCode(HolidayType.OWNERSHIP, parkId);

        return parkData?.code;
      })
    : [];

  const selectedParkNames = filters.parkIds
    ? Array.from(filters.parkIds).map((parkId) => {
        const parkData = getParkByCode(HolidayType.OWNERSHIP, parkId);

        return parkData?.name;
      })
    : [];

  const selectedRegions = filters.parkIds
    ? Array.from(filters.parkIds).map((parkId) => {
        const parkData = getParkByCode(HolidayType.OWNERSHIP, parkId);

        return parkData?.region.name;
      })
    : [];

  const shouldShowSsGroups =
    SmartseerResponseMetadata && SmartseerResponseMetadata?.isInTestGroup !== false;

  return {
    event: EVENTS.VIRTUAL_PAGE_VIEW,
    pageType: PAGE_TYPE,
    pageSubType: 'search',
    pageName: '/ga-virtual/ownership/search/home-results',
    pageId: '(no id)',
    productType: PRODUCT_TYPE,
    parkCode: parseArrayForGTM(
      validSelectedParks && validSelectedParks.length ? validSelectedParks : allParkIds,
      NOT_SELECTED,
      false,
    ),
    parkName: parseArrayForGTM(
      selectedParkNames && selectedParkNames.length ? selectedParkNames : allParkNames,
      NOT_SELECTED,
    ),
    parkRegion: parseArrayForGTM(
      selectedRegions && selectedRegions.length ? selectedRegions : allParkRegions,
      NOT_SELECTED,
    ),
    sortBy: getSortByLabel(filters.sort).toLowerCase(),
    resultsView: 'list view', // Notes: map view is not exists yet
    filterPrice: getPrice({
      min: filters.price?.min,
      max: filters.price?.max,
      type: filters.price?.min || filters.price?.max ? 'total' : undefined,
    }),
    filterBedrooms: bedrooms.length ? `${bedrooms.join('|')}` : NOT_APPLIED,
    filterType: types.length ? `${types.join('|')}` : NOT_APPLIED,
    filterStatus: statuses.length ? `${statuses.join('|')}` : NOT_APPLIED,
    filterManufacturer: manufacturers.length ? `${manufacturers.join('|')}` : NOT_APPLIED,
    resultsBatch: calculateResultsBatch(caravans, page),
    numResults: count,
    numSavedVans,
    products: caravans
      .filter((_, index) => index < 5)
      .map((caravan, index) =>
        getCaravanInfoAmplitude(caravan, {
          position: getPageFirstItemIndex(page) + index + 1,
          list: HOME_RESULTS,
        }),
      ),
    pageFirstPublished: '(not set)',
    pageLastPublished: '(not set)',
    pageAge: '(not set)',
    pagePublishedVersion: '(not set)',
    ...(shouldShowSsGroups && {
      ss_groups: stringifyAndEscapeQuotes(SmartseerResponseMetadata),
    }),
  };
};

export const getCaravansBatchNumber = (startPosition: number) => {
  const endPosition = startPosition + 5;
  const formatPosition = (position: number) =>
    position.toLocaleString('en-US', {
      minimumIntegerDigits: 6,
      useGrouping: false,
    });

  const formattedStart = formatPosition(startPosition + 1);
  const formattedEnd = formatPosition(endPosition);

  return `${formattedStart}-${formattedEnd}`;
};

export const getCaravanBatchDataLayer =
  (caravans: CaravanResponse[], numSavedVans: number) =>
  (
    startPosition: number,
    page = 1,
  ): { [key: string]: string | Record<string, any> | undefined } => {
    const resultsBatch = getCaravansBatchNumber(startPosition);
    return {
      event: EVENTS.VISITOR_INTERACTION,
      interactionName: INTERACTION_NAMES.ACCOMMODATION_IMPRESSION,
      productType: PRODUCT_TYPE,
      resultsBatch,
      numSavedVans: numSavedVans.toString(),
      products: caravans
        .filter((_, index) => {
          const position = getPageFirstItemIndex(page) + index;
          return position >= startPosition && position < startPosition + 5;
        })
        .map((caravan, index) =>
          getCaravanInfoAmplitude(caravan, {
            position: startPosition + index + 1,
            list: HOME_RESULTS,
          }),
        ),
    };
  };

export const getCaravanSelectionDataLayer = (
  caravan: CaravanResponse,
  position: number,
  isButton: boolean,
  fromShortlisting?: boolean,
) => ({
  event: EVENTS.VISITOR_INTERACTION,
  interactionName: INTERACTION_NAMES.ACCOMMODATION_SELECT,
  productType: PRODUCT_TYPE,
  linkType: isButton ? 'button' : 'card',
  ...(!fromShortlisting && {}),
  products: [
    getCaravanInfoAmplitude(caravan, {
      position,
      list: fromShortlisting ? 'ownership vans shortlist' : 'ownership home results',
    }),
  ],
});

export const getButtonClickDataLayer = (
  interactionName: string,
  label: string,
  item?: CaravanResponse,
  showLinkText = true,
  overlayType?: 'alert' | 'content',
  overlayName?: string,
) => ({
  event: EVENTS.VISITOR_INTERACTION,
  interactionName,
  ...(showLinkText ? { linkText: label.toLowerCase() } : {}),
  ...(item ? { products: [getCaravanInfoAmplitude(item)] } : {}),
  ...(interactionName === INTERACTION_NAMES.CLICK_TO_CALL ? { phoneNumber: '03332021456' } : {}),
  ...(overlayType ? { overlayType } : {}),
  ...(overlayName ? { overlayName } : {}),
});

export const getCaravanShareData = (item: CaravanResponse) => ({
  event: 'visitor interaction',
  interactionName: 'accommodation share',
  productType: 'ownership',
  products: [getCaravanInfoAmplitude(item)],
});
